import React, {forwardRef, useImperativeHandle, useRef, useState} from 'react'

import IconButton from '../buttons/IconButton.js'
import {FieldProps, FieldRef, InvalidProps} from './types.js'
import FieldBox from './FieldBox.js'
import {fieldControlClass} from './fieldStyles.js'
import {useFieldId} from './utils.js'
import {field, getFieldCursor, getFieldTextColor, label as labelTokens} from './styles.js'

// Typing and the -/+ buttons both give a plain object with target.value as the number, clamped to min
export interface StepsChangeEvent {
    target: {value: number}
}

export interface StepsProps extends FieldProps, InvalidProps {
    id?: string
    value?: number
    onChange?: (event: StepsChangeEvent) => void
    min?: number
}

export interface StepsRef extends FieldRef {
    getRef: () => HTMLInputElement | null
}

const Steps = forwardRef<StepsRef, StepsProps>(function Steps(props, ref) {
    const {value, label, info, disabled, readOnly, isInvalid, style} = props

    const [focused, setFocused] = useState(false)
    const [isInvalidState, setIsInvalidState] = useState(false)

    const inputRef = useRef<HTMLInputElement | null>(null)
    const fieldId = useFieldId(props.id)

    useImperativeHandle(ref, () => ({
        focus: () => inputRef.current?.focus(),
        blur: () => inputRef.current?.blur(),
        getRef: () => inputRef.current,
        setInvalid: (isInvalid = true) => {
            setIsInvalidState(isInvalid)
            inputRef.current?.focus()
        }
    }), [])

    const onChange = (newValue: number) => {
        const {onChange, min} = props

        if (disabled || readOnly) {
            return
        }

        setIsInvalidState(false)

        if (newValue < min) {
            newValue = min
        }

        onChange?.({target: {value: newValue}})
    }

    // An empty value steps from min (or 0), undefined + 1 would give NaN
    const step = (delta: number) => {
        const current = typeof value === 'number' && !isNaN(value) ? value : (props.min ?? 0)

        onChange(current + delta)
    }

    const focusedState = focused || value || value === 0

    // The box (border, background) is drawn by FieldBox, the input only places the number between the buttons
    const inputStyle: React.CSSProperties = {
        fontSize: field.fontSize,
        height: '100%',
        width: '100%',
        minWidth: 0,
        paddingTop: field.paddingTop,
        paddingBottom: field.paddingBottom,
        outline: 'none',
        border: 'none',
        background: 'transparent',
        color: getFieldTextColor({disabled, readOnly}),
        cursor: getFieldCursor({disabled, readOnly})
    }

    // readOnly keeps the field's look but hides the -/+ buttons (keeping their space), so nothing suggests it can be changed
    const buttonStyle: React.CSSProperties | undefined = readOnly ? {visibility: 'hidden'} : undefined

    return (
        <FieldBox
            label={label}
            info={info}
            floated={!!focusedState}
            focused={focused}
            invalid={isInvalid || isInvalidState}
            disabled={disabled}
            readOnly={readOnly}
            // Keeps the label above the number instead of above the - button, resting and floated
            labelPaddingLeft={labelTokens.paddingLeftPrepend}
            // The + button
            trailingWidth={30}
            controlId={fieldId}
            style={style}
        >
            <IconButton
                disabled={disabled || readOnly}
                style={buttonStyle}
                // Clicking quickly in a row steps every click
                noClickGuard
                onClick={() => step(-1)}
                icon='mdi mdi-minus'
                aria-label='Verlagen'
            />
            <input
                className={fieldControlClass}
                id={fieldId}
                style={inputStyle}
                type='number'
                value={value === 0 ? 0 : (value || '')}
                disabled={disabled}
                // Not disabled when readOnly, so the value can still be focused and selected
                readOnly={readOnly}
                onFocus={() => {
                    if (!disabled && !readOnly) setFocused(true)
                }}
                onBlur={() => setFocused(false)}
                onChange={(event) => onChange(parseInt(event.target.value || '0') || 0)}
                ref={inputRef}
            />
            <IconButton
                disabled={disabled || readOnly}
                style={buttonStyle}
                // Clicking quickly in a row steps every click
                noClickGuard
                onClick={() => step(1)}
                icon='mdi mdi-plus'
                aria-label='Verhogen'
            />
        </FieldBox>
    )
})

export default Steps
